import { Badge } from "@/components/ui/badge";
import { Can } from "@/components/Can";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import CartItem from "@/components/CartItem";
import TransactionStamp from "@/components/TransactionStamp";
import { ScanBarcode, Tag, DollarSign, CreditCard, Smartphone, ShoppingBag, Wallet } from "lucide-react";

export default function CartPanel({
  cart,
  scanInput,
  setScanInput,
  scanInputRef,
  handleScan,
  cartEndRef,
  updateQuantity,
  removeFromCart,
  handleCartItemClick,
  selectedCartItemId,
  displaySettings,
  transactionComplete,
  completedPaymentMethod,
  completedAmountTendered,
  completedChangeDue,
  mixMatchDiscount,
  buydownDetails,
  mixMatchSavings,
  buydownSavings,
  subtotal,
  tax,
  deposit,
  total,
  setShowPaymentDialog,
  onQuickPay,
  fsEligibleTotal,
  surchargeAmount,
  minAmount,
  hasSurchargeInCart,
  onAddSurcharge,
}) {
  const belowMin = minAmount > 0 && total < minAmount && !hasSurchargeInCart;
  return (
    <div
      className="flex flex-col overflow-hidden shadow-xl z-10"
      data-testid="cart-sidebar"
      style={{
        backgroundColor: displaySettings.cart_bg_color || "#FFFFFF",
        border: `2px solid ${displaySettings.cart_border_color || "#E4E4E7"}`,
        borderRadius: "8px",
        margin: "4px 8px 4px 8px",
        flex: 1,
      }}
    >
      {/* Scan Bar */}
      <div
        className="p-3 flex-shrink-0"
        style={{
          backgroundColor: displaySettings.scan_bg_color || "#FFFFFF",
          border: `2px solid ${displaySettings.scan_border_color || "#E4E4E7"}`,
          borderRadius: "8px",
          margin: "6px",
        }}
      >
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <ScanBarcode className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
            <Input
              ref={scanInputRef}
              type="text"
              placeholder="Scan barcode or enter SKU..."
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              onKeyDown={handleScan}
              className="pl-10 h-10 text-base bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:ring-2 focus:ring-blue-500"
              data-testid="scan-input"
              autoFocus
            />
          </div>
          <Badge variant="secondary" className="text-base px-2 py-1 h-10 flex items-center">
            {cart.reduce((sum, item) => sum + item.quantity, 0)}
          </Badge>
        </div>
      </div>

      {/* Cart Items */}
      <ScrollArea className="flex-1 min-h-0 p-3 relative" scrollbarColor="bg-blue-400">
        {cart.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">Cart is empty</div>
        ) : (
          <div className={`space-y-2 ${transactionComplete ? "opacity-90" : ""}`}>
            {cart.map((item, index) => {
              const hasMixMatch = mixMatchDiscount.applied_discounts?.some((d) =>
                d.applied_products?.includes(item.product_id)
              ) || false;
              const hasBuydown = buydownDetails.some((b) => b.product_id === item.product_id);
              let itemDiscount = 0;
              const mmInfo = mixMatchDiscount.applied_discounts?.find((d) =>
                d.applied_products?.includes(item.product_id)
              );
              if (mmInfo) itemDiscount += (mmInfo.savings || 0) * (item.subtotal / (mmInfo.original_total || 1));
              const bdInfo = buydownDetails.find((b) => b.product_id === item.product_id);
              if (bdInfo) itemDiscount += (bdInfo.buydown_amount || 0) * item.quantity;

              return (
                <CartItem
                  key={item.product_id}
                  item={item}
                  index={index}
                  onUpdateQuantity={updateQuantity}
                  onRemove={removeFromCart}
                  onSelect={handleCartItemClick}
                  displaySettings={displaySettings}
                  hasMixMatch={hasMixMatch}
                  hasBuydown={hasBuydown}
                  isSelected={selectedCartItemId === item.product_id}
                  discount={itemDiscount}
                />
              );
            })}
            <div ref={cartEndRef} />
          </div>
        )}
        <TransactionStamp isComplete={transactionComplete} paymentMethod={completedPaymentMethod} />
      </ScrollArea>

      {/* Cart Summary */}
      <div
        className="flex-shrink-0 px-3 py-1"
        style={{
          backgroundColor: displaySettings.cart_bg_color || "#FFFFFF",
          border: `2px solid ${displaySettings.cart_border_color || "#E4E4E7"}`,
          borderRadius: "8px",
          margin: "2px 6px 2px 6px",
        }}
      >
        {cart.length > 0 ? (
          <>
            {/* Compact pink summary line */}
            <div className="flex items-center justify-between px-3 py-1.5 rounded-lg mb-2" style={{ backgroundColor: '#EC4899', color: '#FFFFFF' }}>
              <span className="text-sm font-bold" style={{ fontFamily: "Roboto Slab, serif" }}>
                Subtotal: ${subtotal.toFixed(2)}
              </span>
              <span className="text-sm font-bold" style={{ fontFamily: "Roboto Slab, serif" }}>|</span>
              <span className="text-sm font-bold" style={{ fontFamily: "Roboto Slab, serif" }}>
                Tax: ${tax.toFixed(2)}
              </span>
              <span className="text-sm font-bold" style={{ fontFamily: "Roboto Slab, serif" }}>|</span>
              <span className="text-sm font-bold" style={{ fontFamily: "Roboto Slab, serif" }}>
                Total: ${total.toFixed(2)}
              </span>
            </div>
            {deposit > 0 && (
              <div className="flex justify-between text-sm mb-1 px-1">
                <span className="text-zinc-600" style={{ fontFamily: "Roboto Slab, serif" }}>Deposit</span>
                <span className="font-mono font-bold" style={{ fontFamily: "Roboto Slab, serif" }}>${deposit.toFixed(2)}</span>
              </div>
            )}
            {mixMatchSavings > 0 && (
              <div className="flex items-center justify-between bg-yellow-50 rounded-lg px-3 py-2 border border-yellow-300 mb-1">
                <div className="flex items-center gap-1">
                  <Tag className="w-3 h-3 text-yellow-600" />
                  <span className="text-sm font-bold text-yellow-700">Mix n' Match Saving!</span>
                </div>
                <span className="text-sm font-bold text-yellow-700 font-mono" data-testid="mix-match-savings">You Saved: -${mixMatchSavings.toFixed(2)}</span>
              </div>
            )}
            {buydownSavings > 0 && (
              <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2 border border-blue-300 mb-1">
                <div className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3 text-blue-600" />
                  <span className="text-sm font-bold text-blue-700">MFG Saving!</span>
                </div>
                <span className="text-sm font-bold text-blue-700 font-mono">You Saved: -${buydownSavings.toFixed(2)}</span>
              </div>
            )}
            {transactionComplete ? (
              <div className="w-full bg-zinc-100 border border-zinc-200 rounded-xl p-3 flex justify-between items-center">
                <div className="text-center flex-1">
                  <p className="text-xs text-zinc-500 uppercase font-medium" style={{ fontFamily: "Oswald, sans-serif" }}>Paid</p>
                  <p className="text-3xl font-bold text-green-600" style={{ fontFamily: "Oswald, sans-serif" }}>${completedAmountTendered.toFixed(2)}</p>
                </div>
                <div className="w-px h-12 bg-zinc-300"></div>
                <div className="text-center flex-1">
                  <p className="text-xs text-zinc-500 uppercase font-medium" style={{ fontFamily: "Oswald, sans-serif" }}>Change</p>
                  <p className="text-3xl font-bold text-red-600" style={{ fontFamily: "Oswald, sans-serif" }}>${completedChangeDue.toFixed(2)}</p>
                </div>
              </div>
            ) : (
              <>
                {/* Quick Payment Buttons (toggled via Settings > Display) */}
                {displaySettings.show_quick_pay_buttons !== false && (
                <>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  <Can k="pos.cart.quick_credit"><Button
                    onClick={() => {
                      if (belowMin && surchargeAmount > 0 && onAddSurcharge) onAddSurcharge(surchargeAmount);
                      onQuickPay?.("CREDIT");
                    }}
                    className="h-12 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl active:scale-95 transition-transform"
                    data-testid="quick-credit-btn"
                  >
                    <div className="flex flex-col items-center leading-tight">
                      <span className="flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> CREDIT</span>
                      {belowMin && surchargeAmount > 0 && (
                        <span className="text-[9px] font-normal opacity-90">+${surchargeAmount.toFixed(2)} FEE</span>
                      )}
                    </div>
                  </Button></Can>
                  <Can k="pos.cart.quick_debit"><Button
                    onClick={() => {
                      if (belowMin && surchargeAmount > 0 && onAddSurcharge) onAddSurcharge(surchargeAmount);
                      onQuickPay?.("DEBIT");
                    }}
                    className="h-12 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-xl active:scale-95 transition-transform"
                    data-testid="quick-debit-btn"
                  >
                    <div className="flex flex-col items-center leading-tight">
                      <span className="flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> DEBIT</span>
                      {belowMin && surchargeAmount > 0 && (
                        <span className="text-[9px] font-normal opacity-90">+${surchargeAmount.toFixed(2)} FEE</span>
                      )}
                    </div>
                  </Button></Can>
                  <Can k="pos.cart.quick_ebt_food"><Button
                    onClick={() => onQuickPay?.("EBT_FOOD")}
                    className="h-12 text-xs font-bold bg-green-600 hover:bg-green-700 text-white rounded-xl active:scale-95 transition-transform"
                    data-testid="quick-ebt-food-btn"
                    disabled={fsEligibleTotal <= 0}
                  >
                    <div className="flex flex-col items-center leading-tight">
                      <span className="flex items-center gap-1"><ShoppingBag className="w-3.5 h-3.5" /> EBT FOOD</span>
                      {fsEligibleTotal > 0 && (
                        <span className="text-[9px] font-normal opacity-90">${fsEligibleTotal.toFixed(2)}</span>
                      )}
                    </div>
                  </Button></Can>
                  <Can k="pos.cart.quick_ebt_cash"><Button
                    onClick={() => onQuickPay?.("EBT_CASH")}
                    className="h-12 text-xs font-bold bg-teal-600 hover:bg-teal-700 text-white rounded-xl active:scale-95 transition-transform"
                    data-testid="quick-ebt-cash-btn"
                  >
                    <div className="flex flex-col items-center leading-tight">
                      <span>EBT CASH</span>
                    </div>
                  </Button></Can>
                </div>
                {/* Surcharge Notice */}
                {belowMin && surchargeAmount > 0 && (
                  <div className="text-center text-xs text-amber-600 bg-amber-50 rounded-lg py-1 mb-2">
                    Below ${minAmount.toFixed(2)} minimum — ${surchargeAmount.toFixed(2)} FEE applies for Credit/Debit
                  </div>
                )}
                </>
                )}
                {/* Full PAY button */}
                <Can k="pos.cart.checkout"><Button
                  onClick={() => setShowPaymentDialog(true)}
                  className="w-full h-16 font-bold bg-blue-900 hover:bg-blue-950 text-white rounded-xl shadow-lg active:scale-95 transition-transform mb-1"
                  style={{ fontFamily: "Roboto Slab, serif", fontSize: "42px" }}
                  data-testid="checkout-button"
                >
                  PAY ${total.toFixed(2)}
                </Button></Can>
              </>
            )}
          </>
        ) : (
          <div className="text-center py-3 text-zinc-400">Add items to cart</div>
        )}
      </div>
    </div>
  );
}
